import { format } from 'date-fns';
import { useCallback, useMemo, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { ClassifierData } from 'types/classifiers';
import { CorrespondenceDirectionType, CorrespondenceForm, CorrespondenceStatusType } from 'types/correspondence';
import { MatterMinWithClientParty } from 'types/matters';
import { DEFAULT_DATE_FORMAT } from 'utils/parseDate';

const emptySenderOrRecipient = { partyPerson: null, partyCompany: null, correspondencePartyAddress: null };

interface Props {
    type: 'add' | 'edit';
    initialData?: CorrespondenceForm;
    classifiers: Record<string, ClassifierData>;
}

/**
 * The correspondence form's state, shared by the create and edit pages and by the record's sections: the form, with
 * at least one sender or recipient entry to fill in, what the status and direction make required, and the clients
 * a chosen case brings along.
 */
export const useCorrespondenceForm = ({ type, initialData, classifiers }: Props) => {
    const [now] = useState(() => Date.now());
    const defaultValues: CorrespondenceForm = {
        readOnlyNumber: '',
        name: '',
        typeCode: null,
        directionTypeCode: CorrespondenceDirectionType.INCOMING,
        categoryTypeCode: null,
        date: format(now, DEFAULT_DATE_FORMAT),
        documentDate: format(now, DEFAULT_DATE_FORMAT),
        isOfficial: false,
        officialReference: '',
        employees: [],
        sendersOrRecipients: [emptySenderOrRecipient],
        note: '',
        isHandled: false,
        statusTypeCode: null,
        clients: [],
        matters: [],
        assignments: [],
        dmsLink: '',
    };

    const getFormValues = (data: CorrespondenceForm | undefined): CorrespondenceForm => {
        if (!data) return defaultValues;
        return {
            ...defaultValues,
            ...data,
            sendersOrRecipients:
                data.sendersOrRecipients?.length > 0 ? data.sendersOrRecipients : [emptySenderOrRecipient],
        };
    };

    const methods = useForm<CorrespondenceForm>({
        defaultValues: getFormValues(initialData),
        values: initialData ? getFormValues(initialData) : undefined,
    });

    const { control, setValue } = methods;

    const directionTypeCode = useWatch({ control, name: 'directionTypeCode' });
    const isOfficial = useWatch({ control, name: 'isOfficial' });
    const statusTypeCode = useWatch({ control, name: 'statusTypeCode' });
    const clients = useWatch({ control, name: 'clients' });
    const matters = useWatch({ control, name: 'matters' });

    const nonDraftRequired = statusTypeCode?.value !== CorrespondenceStatusType.DRAFT;

    const recipientOrSenderString = directionTypeCode === CorrespondenceDirectionType.INCOMING ? 'recipient' : 'sender';

    const selectedClientIds = useMemo(() => clients.map((p) => p.id).join(','), [clients]);

    const selectedMatterIds = useMemo(() => matters?.map((m) => m.id).join(','), [matters]);

    const handleOnMattersChange = useCallback(
        (matters: MatterMinWithClientParty[] | null) => {
            if (matters && matters.length > 0) {
                const currentClients = methods.getValues('clients') || [];
                const clientIds = new Set(currentClients.map((c) => c.id));

                const newClients = matters
                    .filter((matter) => matter.clientParty)
                    .map((matter) => matter.clientParty!)
                    .filter((clientParty) => !clientIds.has(clientParty.id));

                if (newClients.length > 0) {
                    setValue('clients', [...currentClients, ...newClients]);
                }
            }
        },
        [methods, setValue]
    );

    return {
        type,
        methods,
        classifiers,
        directionTypeCode,
        isOfficial,
        matters,
        nonDraftRequired,
        recipientOrSenderString,
        selectedClientIds,
        selectedMatterIds,
        handleOnMattersChange,
    };
};

export type CorrespondenceFormState = ReturnType<typeof useCorrespondenceForm>;
